/**
 * SR-01 N-gram self-repetition (INV-CH-SR-01, ENGINE-SPEC §3.3, §4; change C-D6-SR01).
 *
 * The seed counted repeated 4-grams over the whole document (a type-count that grows with length,
 * D6). Here every 4-gram position looks back to its previous occurrence inside the SAME SECTION;
 * the recurrence-distance distribution is estimated with Kaplan–Meier (censored at the section
 * start) and the seed's statistic at its own reference size (500 words) is derived from it:
 * value = (1/500)·Σ_{k<500} R(k). The seed ramp then applies unchanged.
 *
 * Per-paragraph series (segmentation, hotspot tests): an event is a 4-gram position whose nearest
 * previous occurrence is d < n1 words back, weighted by (n1 − d)/n1 (n1 = the effective reference
 * size, 500 unless the text is short). The weight is the exact expected contribution of that
 * position to the seed statistic of a random n1-word text, so ΣK/ΣE is on the same scale as the
 * displayed value and as the prior μ (see the file note in the W3a derived-channel report).
 */
import type { Doc } from '../document/types.ts';
import {
	type RecurrenceCurve,
	type SeedEquivalent,
	hashWindow,
	recurrenceAt,
	scanRecurrence,
} from '../segments/recurrence.ts';
import {
	abstainResult,
	derivePrior,
	languageGate,
	overdispersion,
	scoredResult,
} from './framework.ts';
import {
	type ProseWordSeq,
	SMALL,
	addRunSpans,
	greenwoodSe,
	makeEvidence,
	minAtRiskFor,
	proseWordSeq,
	seedEquivalentAbsorbing,
} from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

const RAMP: Ramp = [
	[0, 18],
	[0.01, 32],
	[0.03, 55],
	[0.06, 74],
	[0.12, 90],
];
const N0 = 500;
const GRAM = 4;

export const sr01Def: ChannelDef = {
	id: 'SR-01',
	name: 'N-gram self-repetition',
	family: 'REUSE',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'recurrence',
	features: [{ id: 'repeated4gram', label: 'Repeated 4-gram share (500-word reference)' }],
	minExposure: 80,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'share of 4-grams repeated within a 500-word lookback',
	prior: derivePrior(RAMP, 1, 'words', 1.5),
	weight: 0.7,
	nd: 0.5,
	changes: ['C-D6-SR01'],
};

interface Sr01Scan {
	seq: ProseWordSeq;
	W: number;
	keys: Float64Array;
	curve: RecurrenceCurve;
	est: SeedEquivalent;
	/** Effective reference size used for the event weights (the baseline's, when frozen). */
	n1: number;
	k: Float64Array;
	e: Float64Array;
	weight: (i: number) => number;
}

/** The 4-gram scan shared by `run` (document value) and `measure` (bench event scan, frozen n1). */
function scanDoc(doc: Doc, frozenN1?: number): Sr01Scan {
	const seq = proseWordSeq(doc);
	const n = seq.count;
	// one key per 4-gram window; a window belongs to the section of its LAST word
	const W = n - GRAM + 1;
	const keys = new Float64Array(n);
	const boundary = new Int32Array(n);
	for (let i = 0; i < W; i++) {
		keys[i] = hashWindow(seq.id, i, GRAM);
		boundary[i] = seq.secStart[i + GRAM - 1] as number;
	}
	const scan = scanRecurrence(keys, boundary, N0);
	const curve = scan.curve;
	const est = seedEquivalentAbsorbing(curve, N0, minAtRiskFor(curve.n));

	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const dist = scan.dist;
	const n1 = frozenN1 ?? est.n1;
	const weight = (i: number): number => {
		const d = dist[i] as number;
		return d > 0 && d < n1 ? (n1 - d) / n1 : 0;
	};
	for (let i = 0; i < W; i++) {
		const p = seq.para[i + GRAM - 1] as number;
		e[p] = (e[p] as number) + 1;
		const w = weight(i);
		if (w > 0) k[p] = (k[p] as number) + w;
	}
	return { seq, W, keys, curve, est, n1, k, e, weight };
}

/** Event scan of one (context) document: every 4-gram position and its recurrence weight (BENCH-V2-SPEC §5.1). */
function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const { seq, W, k, e, weight } = scanDoc(
		doc,
		ctx.frozen?.channels['SR-01']?.features.referenceSize
	);
	const ev = makeEvidence();
	addRunSpans(doc, ev, seq, (i) => weight(i) > 0, W, GRAM, weight);
	return { k, e, evidence: ev };
}

function run(ctx: RunContext): ChannelResult {
	const def = sr01Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const n = proseWordSeq(doc).count;
	if (n < def.minExposure)
		return abstainResult(def, `insufficient text: ${n} words < ${def.minExposure}`, notes);

	const { seq, W, keys, curve, est, n1, k, e, weight } = scanDoc(doc);
	if (est.shortLookback) notes.push('shortLookback');
	const P = doc.paragraphs.count;
	let K = 0;
	let E = 0;
	for (let p = 0; p < P; p++) {
		K += k[p] as number;
		E += e[p] as number;
	}

	const phi = overdispersion(k, e, K, E, def.prior.phi0);
	const value = est.value;
	const se = greenwoodSe(curve, n1) * Math.sqrt(phi);
	const ci90: [number, number] = [Math.max(0, value - 1.645 * se), value + 1.645 * se];
	const kappa = (E / (E + def.prior.m)) * est.kappaFactor;

	// evidence: merged runs of event positions; summary: top repeated 4-grams (second pass, events only)
	const ev = makeEvidence();
	addRunSpans(doc, ev, seq, (i) => weight(i) > 0, W, GRAM, weight);
	const grams = new Map<number, { first: number; count: number }>();
	for (let i = 0; i < W; i++) {
		if (weight(i) <= 0) continue;
		const key = keys[i] as number;
		const g = grams.get(key);
		if (g) g.count++;
		else grams.set(key, { first: i, count: 2 });
	}
	const top = [...grams.values()]
		.sort((a, b) => b.count - a.count || a.first - b.first)
		.slice(0, 6)
		.map((g) => {
			const words: string[] = [];
			for (let j = 0; j < GRAM; j++) words.push(doc.tokenText(seq.tok[g.first + j] as number));
			return `“${words.join(' ')}” ${g.count}×`;
		});

	return scoredResult(def, {
		K,
		E,
		raw: value,
		estimate: value,
		ci90,
		deviation: (def.direction * (value - def.prior.mu)) / Math.max(se, SMALL),
		phi,
		kappa,
		perParagraph: { k, e },
		evidence: ev.build(),
		notes,
		features: {
			repeated4gram: value,
			recurrenceAtReference: recurrenceAt(curve, n1 - 1),
			referenceSize: n1,
			positions: curve.n,
		},
		summary: top,
	});
}

export const sr01: ChannelImpl = { def: sr01Def, phase: 'event', run, measure };
